import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { mapFeatureDto, mapQueryResult } from '@/shared/data/dtoMappers';
import { getErrorMessage } from '@/shared/data/errors';
import { mergeProjectFilters } from '@/shared/data/queryFilters';
import {
  buildEqFilter,
  buildSlugFilters,
  type GridFilters,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import type {
  FeaturePlannedCalendarDto,
  FeatureReleasedCalendarDto,
  FeatureDto,
  FeatureFilterCountsDto,
  FeatureSummaryDto,
  QueryFilter,
  QueryResult,
  QuerySort,
} from '@flowqa/shared';

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (): string => {
  const token = getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

export const queryFeatures = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: { page: number; pageSize: number; sorts: QuerySort[]; filters: QueryFilter[] },
): Promise<QueryResult<FeatureDto>> => {
  const filters: GridFilters = input?.filters ?? [];
  const combinedFilters = mergeProjectFilters(filters, projectId ?? null);
  try {
    const result = await apiClient.post<QueryResult<FeatureDto>>(
      'features/query',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 50,
        sorts: input?.sorts ?? [{ field: 'name', direction: 'asc' }],
        filters: combinedFilters,
      },
      { retries: 1, orgId: organisationId ?? undefined },
    );
    return mapQueryResult(result, mapFeatureDto);
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryFeatureSummary = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: { page: number; pageSize: number; sorts: QuerySort[]; filters: QueryFilter[] },
): Promise<FeatureSummaryDto> => {
  const filters: GridFilters = input?.filters ?? [];
  const combinedFilters = mergeProjectFilters(filters, projectId ?? null);
  try {
    return await apiClient.post<FeatureSummaryDto>(
      'features/summary',
      {
        summaryScope: 'full_filtered_scope',
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 1,
        sorts: input?.sorts ?? [],
        filters: combinedFilters,
      },
      { retries: 1, orgId: organisationId ?? undefined },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryFeatureFilterCounts = async (
  projectId: string | null | undefined,
  organisationId: string | null | undefined,
  input: {
    search?: string;
    filters: {
      statuses: GridFilters;
      tags: GridFilters;
      projects: GridFilters;
    };
  },
): Promise<FeatureFilterCountsDto> => {
  const token = ensureToken();
  try {
    return await apiClient.post<FeatureFilterCountsDto>(
      'features/filter-counts',
      {
        search: input.search,
        filters: {
          statuses: mergeProjectFilters(input.filters.statuses, projectId ?? null),
          tags: mergeProjectFilters(input.filters.tags, projectId ?? null),
          projects: mergeProjectFilters(input.filters.projects, projectId ?? null),
        },
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryFeaturePlannedCalendar = async (
  projectId: string | null,
  organisationId: string | null,
  input: { start: string; end: string },
): Promise<FeaturePlannedCalendarDto> => {
  try {
    return await apiClient.post<FeaturePlannedCalendarDto>(
      'features/plannedCalendar',
      { ...input, projectId: projectId ?? undefined },
      { retries: 1, orgId: organisationId ?? undefined },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryFeatureReleasedCalendar = async (
  projectId: string | null,
  organisationId: string | null,
  input: { start: string; end: string },
): Promise<FeatureReleasedCalendarDto> => {
  try {
    return await apiClient.post<FeatureReleasedCalendarDto>(
      'features/releasedCalendar',
      { ...input, projectId: projectId ?? undefined },
      { retries: 1, orgId: organisationId ?? undefined },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const getFeatureBySlug = async (
  slug: string | null,
  projectId?: string | null,
  organisationId?: string | null,
): Promise<FeatureDto | null> => {
  if (!slug) {
    return null;
  }
  const slugFilters = buildSlugFilters(slug);
  const filters =
    projectId != null
      ? mergeFilters(slugFilters, buildEqFilter('projectId', projectId))
      : slugFilters;
  try {
    const payload = await apiClient.post<QueryResult<FeatureDto>>(
      'features/query',
      { page: 1, pageSize: 1, filters },
      { retries: 1, orgId: organisationId ?? undefined },
    );
    const mapped = mapQueryResult(payload, mapFeatureDto);
    return mapped.items[0] ?? null;
  } catch (err) {
    throw toApiError(err);
  }
};

export const createFeature = async (
  input: {
    name: string;
    projectId: string;
    status?: FeatureDto['status'];
    releaseId?: string | null;
    plannedFor?: string | null;
    releasedAt?: string | null;
    tags?: string[];
  },
  organisationId?: string | null,
): Promise<FeatureDto> => {
  const authToken = ensureToken();
  const body = {
    name: input.name.trim(),
    projectId: input.projectId.trim(),
    status: input.status,
    releaseId: input.releaseId === undefined ? undefined : input.releaseId,
    plannedFor: input.plannedFor === undefined ? undefined : input.plannedFor,
    releasedAt: input.releasedAt === undefined ? undefined : input.releasedAt,
    tags: input.tags,
  };
  try {
    const result = await apiClient.post<FeatureDto>('features', body, {
      token: authToken,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapFeatureDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const updateFeature = async (
  input: {
    id: string;
    name?: string;
    status?: FeatureDto['status'];
    releaseId?: string | null;
    plannedFor?: string | null;
    releasedAt?: string | null;
    tags?: string[];
  },
  organisationId?: string | null,
): Promise<FeatureDto> => {
  const authToken = ensureToken();
  const body = {
    name: input.name?.trim(),
    status: input.status,
    releaseId: input.releaseId === undefined ? undefined : input.releaseId,
    plannedFor: input.plannedFor === undefined ? undefined : input.plannedFor,
    releasedAt: input.releasedAt === undefined ? undefined : input.releasedAt,
    tags: input.tags,
  };
  try {
    const result = await apiClient.patch<FeatureDto>(`features/${input.id}`, body, {
      token: authToken,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapFeatureDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const deleteFeature = async (
  input: { id: string },
  organisationId?: string | null,
): Promise<{ id: string }> => {
  const authToken = ensureToken();
  try {
    await apiClient.delete<undefined>(`features/${input.id}`, {
      token: authToken,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};
